import React from 'react';
import { Check, ClipboardCheck, CreditCard, Package, Truck, XCircle } from 'lucide-react';

// Customer-facing 4-step fulfilment progress tracker for the B2B portal.
// Reads exclusively from order.fulfilment_stage (1–4). Shows a cancelled
// banner if the order's internal status is 'cancelled'.

const STAGES = [
  { num: 1, label: 'Order Received', icon: ClipboardCheck },
  { num: 2, label: 'Payment Authorised', icon: CreditCard },
  { num: 3, label: 'Picked & Packaged', icon: Package },
  { num: 4, label: 'Dispatched', icon: Truck },
];

export default function FulfilmentTracker({ order }) {
  const currentStage = Number(order.fulfilment_stage) || 1;
  const isCancelled = order.status === 'cancelled';

  if (isCancelled) {
    return (
      <div className="border border-red-400/30 bg-red-400/[0.03] p-4 flex items-center gap-3">
        <XCircle className="w-5 h-5 text-red-400 shrink-0" />
        <div>
          <p className="text-red-400 text-xs font-bold tracking-widest">ORDER CANCELLED</p>
          <p className="text-white/40 text-[11px] mt-0.5">This order has been cancelled. Please contact us if you have any questions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-white/10 bg-card p-4">
      <p className="text-white/40 text-[10px] font-bold tracking-widest mb-4">ORDER PROGRESS</p>
      <div className="flex items-start gap-1 sm:gap-2">
        {STAGES.map((stage, idx) => {
          const Icon = stage.icon;
          const isDone = currentStage > stage.num;
          const isActive = currentStage === stage.num;
          const isFuture = currentStage < stage.num;

          return (
            <React.Fragment key={stage.num}>
              <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
                <div
                  className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full border-2 flex items-center justify-center transition-all ${
                    isActive
                      ? 'border-primary bg-primary/10 text-primary scale-110'
                      : isDone
                      ? 'border-green-400/40 bg-green-400/10 text-green-400'
                      : 'border-white/10 text-white/20'
                  }`}
                >
                  {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                </div>
                <p
                  className={`text-[9px] sm:text-[10px] font-bold tracking-wider text-center leading-tight transition-colors ${
                    isActive ? 'text-primary' : isDone ? 'text-green-400/80' : 'text-white/25'
                  }`}
                >
                  {stage.label.toUpperCase()}
                </p>
                {isActive && (
                  <span className="text-[8px] text-primary/70 font-bold tracking-widest animate-pulse">IN PROGRESS</span>
                )}
              </div>
              {idx < STAGES.length - 1 && (
                <div className={`h-0.5 w-3 sm:w-6 mt-4 sm:mt-[18px] shrink-0 transition-colors ${currentStage > stage.num ? 'bg-green-400/40' : 'bg-white/5'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}